
import { useState, useCallback, useEffect } from "react";
import financialTransactionService from "../services/FinancialTransactionService";

const PAGE_SIZE = 30;

const useFinancialTransactions = (fixedType = null) => {
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    From: "",
    To: "",
    TransactionType: fixedType ?? "",
    Category: "",
    SearchTerm: "",
    PageIndex: 1,
    PageSize: PAGE_SIZE,
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        ...filters,
        PageIndex: Number(filters.PageIndex) || 1,
        PageSize: Number(filters.PageSize) || PAGE_SIZE,
      };

      if (fixedType !== null && fixedType !== undefined) {
        params.TransactionType = fixedType;
      }

      const res = await financialTransactionService.getAll(params);

      if (res) {
        const data = Array.isArray(res.data) ? res.data : [];

        const count = Number(res.totalCount) || 0;
        const pages =
          Number(res.totalPages) ||
          Math.max(1, Math.ceil(count / params.PageSize));

        setTransactions(data);
        setTotalCount(count);
        setTotalPages(pages);

        setHasPreviousPage(
          typeof res.hasPreviousPage === "boolean"
            ? res.hasPreviousPage
            : params.PageIndex > 1
        );

        setHasNextPage(
          typeof res.hasNextPage === "boolean"
            ? res.hasNextPage
            : params.PageIndex < pages
        );
      } else {
        setTransactions([]);
        setTotalCount(0);
        setTotalPages(1);
        setHasPreviousPage(false);
        setHasNextPage(false);
      }
    } catch (err) {
      setError("حدث خطأ أثناء جلب البيانات المالية");
      setTransactions([]);
      setTotalCount(0);
      setTotalPages(1);
      setHasPreviousPage(false);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [filters, fixedType]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const createTransaction = async (payload) => {
    setError(null);

    const body =
      fixedType !== null && fixedType !== undefined
        ? { ...payload, transactionType: fixedType }
        : payload;

    try {
      const res = await financialTransactionService.create(body, setError);
      await fetchTransactions();
      return res;
    } catch (err) {
      throw err;
    }
  };

  const updateTransaction = async (id, payload) => {
    setError(null);

    const body =
      fixedType !== null && fixedType !== undefined
        ? { ...payload, transactionType: fixedType }
        : payload;

    try {
      const res = await financialTransactionService.update(id, body);
      await fetchTransactions();
      return res;
    } catch (err) {
      setError("حدث خطأ أثناء تحديث المعاملة");
      throw err;
    }
  };

  const deleteTransaction = async (id) => {
    setError(null);

    try {
      const res = await financialTransactionService.remove(id);
      await fetchTransactions();
      return res;
    } catch (err) {
      setError("حدث خطأ أثناء حذف المعاملة");
      throw err;
    }
  };

  return {
    transactions,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    loading,
    error,
    filters,
    setFilters,
    fetchTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  };
};

export default useFinancialTransactions;
